export interface Milestone {
  name: string
  due: string
  status: 'complete' | 'in-progress' | 'upcoming'
}

export interface ActivityEntry {
  at: string
  actor: string
  text: string
}

export interface PortalMessage {
  at: string
  from: string
  fromCavalla: boolean
  text: string
}

export interface Deliverable {
  id: string
  projectId: string
  name: string
  type: string
  version: string
  sizeKb: number
  createdAt: string
  uploadedBy: string
  fileName: string
}

export interface PortalProjectRecord {
  id: string
  organisationId: string
  name: string
  serviceLine: string
  status: 'discovery' | 'in-delivery' | 'in-review' | 'complete'
  summary: string
  startDate: string
  targetDate: string
  progress: number
  milestones: Milestone[]
  activity: ActivityEntry[]
  messages: PortalMessage[]
}

export interface InvoiceRecord {
  id: string
  organisationId: string
  number: string
  amount: number
  currency: string
  status: 'paid' | 'due' | 'overdue'
  issuedAt: string
  dueDate: string
  fileName: string
}

export const PROJECTS: PortalProjectRecord[] = [
  {
    id: 'prj-001',
    organisationId: 'org-northbridge',
    name: 'AI readiness and use case shortlist',
    serviceLine: 'Cavalla AI',
    status: 'in-delivery',
    summary:
      'A scored readiness assessment across data, skills, use cases, governance and budget, followed by a prioritised shortlist of two candidate use cases for the programmes team.',
    startDate: '2026-06-08',
    targetDate: '2026-08-14',
    progress: 62,
    milestones: [
      { name: 'Kick off and evidence request', due: '2026-06-12', status: 'complete' },
      { name: 'Stakeholder interviews', due: '2026-06-30', status: 'complete' },
      { name: 'Scored readiness report', due: '2026-07-24', status: 'in-progress' },
      { name: 'Use case shortlist and briefing', due: '2026-08-14', status: 'upcoming' },
    ],
    activity: [
      { at: '2026-07-22', actor: 'Cavalla team', text: 'Draft readiness report uploaded for your review.' },
      { at: '2026-07-15', actor: 'Cavalla team', text: 'Interview programme completed, eleven stakeholders.' },
      { at: '2026-06-30', actor: 'Ama Mensah', text: 'Provided data inventory for the programmes function.' },
      { at: '2026-06-12', actor: 'Cavalla team', text: 'Kick off session held, scope confirmed.' },
    ],
    messages: [
      {
        at: '2026-07-22',
        from: 'Jonathan Clarke',
        fromCavalla: true,
        text: 'The draft readiness report is now in your deliverables. The governance score is lower than the rest, which is where we would focus first. Happy to walk through it on Thursday.',
      },
      {
        at: '2026-07-23',
        from: 'Ama Mensah',
        fromCavalla: false,
        text: 'Thank you. Thursday at ten works for us. I will bring our head of programmes.',
      },
    ],
  },
  {
    id: 'prj-002',
    organisationId: 'org-northbridge',
    name: 'Donor demand mapping, East Africa',
    serviceLine: 'Cavalla Intelligence',
    status: 'in-review',
    summary:
      'Mapping funder priorities and disbursement patterns across four East African markets, with a costed view of where the trust is best positioned to bid.',
    startDate: '2026-04-20',
    targetDate: '2026-07-31',
    progress: 88,
    milestones: [
      { name: 'Source review and methodology', due: '2026-05-02', status: 'complete' },
      { name: 'Funder mapping', due: '2026-06-06', status: 'complete' },
      { name: 'Positioning analysis', due: '2026-07-04', status: 'complete' },
      { name: 'Final report and board pack', due: '2026-07-31', status: 'in-progress' },
    ],
    activity: [
      { at: '2026-07-18', actor: 'Cavalla team', text: 'Board pack drafted, awaiting your comments.' },
      { at: '2026-07-04', actor: 'Cavalla team', text: 'Positioning analysis delivered.' },
      { at: '2026-06-06', actor: 'Cavalla team', text: 'Funder map delivered across four markets.' },
    ],
    messages: [
      {
        at: '2026-07-18',
        from: 'Jonathan Clarke',
        fromCavalla: true,
        text: 'Board pack is drafted. We have kept the recommendation to three markets rather than four, and the reasoning is in section five.',
      },
    ],
  },
]

export const DELIVERABLES: Deliverable[] = [
  {
    id: 'dlv-001',
    projectId: 'prj-001',
    name: 'AI readiness report, draft',
    type: 'PDF',
    version: 'v0.9',
    sizeKb: 3840,
    createdAt: '2026-07-22',
    uploadedBy: 'Jonathan Clarke',
    fileName: 'northbridge-ai-readiness-draft-v09.pdf',
  },
  {
    id: 'dlv-002',
    projectId: 'prj-001',
    name: 'Stakeholder interview summary',
    type: 'PDF',
    version: 'v1.0',
    sizeKb: 1120,
    createdAt: '2026-07-15',
    uploadedBy: 'Cavalla team',
    fileName: 'northbridge-interview-summary-v1.pdf',
  },
  {
    id: 'dlv-003',
    projectId: 'prj-001',
    name: 'Data inventory template',
    type: 'XLSX',
    version: 'v1.2',
    sizeKb: 288,
    createdAt: '2026-06-14',
    uploadedBy: 'Cavalla team',
    fileName: 'northbridge-data-inventory-v12.xlsx',
  },
  {
    id: 'dlv-004',
    projectId: 'prj-002',
    name: 'Board pack, draft',
    type: 'PDF',
    version: 'v0.8',
    sizeKb: 5620,
    createdAt: '2026-07-18',
    uploadedBy: 'Jonathan Clarke',
    fileName: 'northbridge-board-pack-draft-v08.pdf',
  },
  {
    id: 'dlv-005',
    projectId: 'prj-002',
    name: 'Funder map, four markets',
    type: 'PDF',
    version: 'v1.0',
    sizeKb: 4210,
    createdAt: '2026-06-06',
    uploadedBy: 'Cavalla team',
    fileName: 'northbridge-funder-map-v1.pdf',
  },
  {
    id: 'dlv-006',
    projectId: 'prj-002',
    name: 'Positioning analysis workbook',
    type: 'XLSX',
    version: 'v1.1',
    sizeKb: 962,
    createdAt: '2026-07-04',
    uploadedBy: 'Cavalla team',
    fileName: 'northbridge-positioning-v11.xlsx',
  },
]

export const INVOICES: InvoiceRecord[] = [
  {
    id: 'inv-003',
    organisationId: 'org-northbridge',
    number: 'CS-2026-0142',
    amount: 7500,
    currency: 'USD',
    status: 'due',
    issuedAt: '2026-07-01',
    dueDate: '2026-07-31',
    fileName: 'CS-2026-0142.pdf',
  },
  {
    id: 'inv-002',
    organisationId: 'org-northbridge',
    number: 'CS-2026-0118',
    amount: 6000,
    currency: 'USD',
    status: 'paid',
    issuedAt: '2026-06-01',
    dueDate: '2026-06-30',
    fileName: 'CS-2026-0118.pdf',
  },
  {
    id: 'inv-001',
    organisationId: 'org-northbridge',
    number: 'CS-2026-0094',
    amount: 6000,
    currency: 'USD',
    status: 'paid',
    issuedAt: '2026-05-01',
    dueDate: '2026-05-31',
    fileName: 'CS-2026-0094.pdf',
  },
]

export const STATUS_LABELS: Record<PortalProjectRecord['status'], string> = {
  discovery: 'Discovery',
  'in-delivery': 'In delivery',
  'in-review': 'In review',
  complete: 'Complete',
}
